/**
 * Export Utilities
 * Client-side CSV export functionality
 */

/**
 * Export data to CSV
 * @param {Array} data - Array of objects
 * @param {string} filename - Filename without extension
 * @param {Array} columns - Column definitions [{ key, label }]
 */
export const exportToCSV = (data, filename = 'export', columns = []) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Auto-detect columns if not provided
  const detectedColumns = columns.length > 0 
    ? columns 
    : Object.keys(data[0]).map(key => ({ key, label: key }));

  // Build CSV header
  const header = detectedColumns.map(col => col.label).join(',');

  // Build CSV rows
  const rows = data.map(item => {
    return detectedColumns.map(col => {
      const value = item[col.key];
      // Handle nested objects and arrays
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') return JSON.stringify(value);
      // Escape commas and quotes
      const stringValue = String(value).replace(/"/g, '""');
      return stringValue.includes(',') || stringValue.includes('"') 
        ? `"${stringValue}"` 
        : stringValue;
    }).join(',');
  });

  // Combine header and rows
  const csvContent = [header, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Export statistics data
 * @param {Object} statsData - Statistics data object
 * @param {string} filename - Base filename
 */
export const exportStatsData = (statsData, filename = 'statistics') => {
  const exportData = [];

  // Flatten overview
  if (statsData.overview) {
    exportData.push({
      Metric: 'Total Loads',
      Value: statsData.overview.totalLoads || 0
    });
    exportData.push({
      Metric: 'Delivered',
      Value: statsData.overview.delivered || 0
    });
    exportData.push({
      Metric: 'Revenue',
      Value: statsData.overview.revenue || 0
    });
    exportData.push({
      Metric: 'Expense',
      Value: statsData.overview.expense || 0
    });
    exportData.push({
      Metric: 'Profit',
      Value: statsData.overview.profit || 0
    });
    exportData.push({
      Metric: 'Margin %',
      Value: statsData.overview.margin || 0
    });
  }

  // Export loads by status
  if (statsData.loads?.byStatus) {
    Object.entries(statsData.loads.byStatus).forEach(([status, count]) => {
      exportData.push({
        Metric: `Loads - ${status}`,
        Value: count
      });
    });
  }

  // Export top customers
  if (statsData.tops?.customers) {
    statsData.tops.customers.forEach((customer, index) => {
      exportData.push({
        Metric: `Top Customer ${index + 1}`,
        Value: customer.name || customer.companyName || 'Unknown',
        Revenue: customer.revenue || 0,
        Profit: customer.profit || 0
      });
    });
  }

  exportToCSV(exportData, filename, [
    { key: 'Metric', label: 'Metric' },
    { key: 'Value', label: 'Value' },
    { key: 'Revenue', label: 'Revenue' },
    { key: 'Profit', label: 'Profit' }
  ]);
};
